import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, Plus, FileText, Upload, Trash2, Download, X, Search,
  Briefcase, AlertCircle, CheckCircle2, Loader2, UserCircle, Package, RefreshCw,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLLE_LABELS = {
  dozent: "Dozent/in", gf: "Geschäftsführung", verwaltung: "Verwaltung",
  vertrieb: "Vertrieb", extern: "Extern",
};

function formatBytes(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024*1024) return `${(b/1024).toFixed(0)} KB`;
  return `${(b/1024/1024).toFixed(1)} MB`;
}

export default function Personalakten() {
  const [items, setItems] = useState([]);
  const [kategorien, setKategorien] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("alle"); // alle | aktiv | ausgeschieden
  const [editAkte, setEditAkte] = useState(null);  // für Edit-Modal
  const [detailAkte, setDetailAkte] = useState(null); // für Detail-Modal
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/personalakten`);
      setItems(data.items || []);
      setKategorien(data.kategorien || []);
    } catch (e) {
      toast.error("Fehler beim Laden",
        { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = items.filter(it => {
    if (filter !== "alle" && it.status !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      return ((it.vorname || "") + " " + (it.nachname || "")).toLowerCase().includes(s)
          || (it.email || "").toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="personalakten-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-orange-500" />
            Personalakten
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Mitarbeiter & Dozenten · Qualifikationen, Schulungsnachweise, Verträge
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              if (!window.confirm("Julia Müller und Annika Lorke aus der offiziellen Seed-Datei aktualisieren?\n\n(Qualifikationen, Nachweise, Berufserfahrung werden überschrieben.)")) return;
              try {
                const r = await axios.post(`${API}/personalakten/dozenten-reseed`);
                const rows = r.data?.report || [];
                toast.success(`Dozenten aktualisiert (${rows.length})`, {
                  description: rows.map(x => `${x.vorname} ${x.nachname}: ${x.action}`).join(" · "),
                });
                await load();
              } catch (e) {
                toast.error("Reseed fehlgeschlagen",
                  { description: e?.response?.data?.detail || e.message });
              }
            }}
            data-testid="reseed-dozenten-btn"
            title="Dozenten-Daten (Julia, Annika) aus Seed-Datei aktualisieren"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Dozenten synchronisieren
          </Button>
          <Button onClick={() => setShowCreate(true)} data-testid="create-akte-btn"
                  className="bg-orange-500 hover:bg-orange-600">
            <Plus className="w-4 h-4 mr-2" /> Neue Personalakte
          </Button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Suchen (Name / E-Mail)..." className="pl-9"
                 data-testid="search-input" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle ({items.length})</SelectItem>
            <SelectItem value="aktiv">Aktiv ({items.filter(i => i.status === "aktiv").length})</SelectItem>
            <SelectItem value="ausgeschieden">Ausgeschieden ({items.filter(i => i.status === "ausgeschieden").length})</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-orange-500"/></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">
          Keine Akten gefunden. {search && <span>Suche anpassen.</span>}
        </CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="akten-grid">
          {filtered.map(it => (
            <Card key={it.id} className="hover:shadow-md transition-shadow cursor-pointer group"
                  onClick={() => setDetailAkte(it)} data-testid={`akte-card-${it.id}`}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start justify-between mb-2">
                  <UserCircle className="w-10 h-10 text-orange-300" />
                  {it.status === "aktiv" ? (
                    <Badge className="bg-green-100 text-green-700 border-green-200">aktiv</Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-slate-100 text-slate-600">ausgeschieden</Badge>
                  )}
                </div>
                <h3 className="font-semibold text-slate-900 text-lg">
                  {it.vorname} {it.nachname}
                </h3>
                <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                  <Briefcase className="w-3 h-3" />
                  {ROLLE_LABELS[it.rolle] || it.rolle}
                </p>
                {it.email && <p className="text-xs text-slate-400 mt-1 truncate">{it.email}</p>}
                {it.status === "ausgeschieden" && it.austritt_datum && (
                  <p className="text-xs text-orange-600 mt-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Austritt: {it.austritt_datum}
                  </p>
                )}
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                  <span className="text-slate-500">
                    <FileText className="w-3 h-3 inline mr-1" />
                    {(it.anhaenge || []).length} Dokumente
                  </span>
                  <span className="text-orange-500 group-hover:underline">Öffnen →</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail/Edit Modals */}
      {detailAkte && (
        <PersonalakteDetail
          akte={detailAkte}
          kategorien={kategorien}
          onClose={() => setDetailAkte(null)}
          onEdit={() => { setEditAkte(detailAkte); setDetailAkte(null); }}
          onReload={async () => { await load(); const fresh = (await axios.get(`${API}/personalakten`)).data.items.find(i => i.id === detailAkte.id); setDetailAkte(fresh || null); }}
        />
      )}
      {(showCreate || editAkte) && (
        <PersonalakteForm
          akte={editAkte}
          onClose={() => { setShowCreate(false); setEditAkte(null); }}
          onSaved={async () => { setShowCreate(false); setEditAkte(null); await load(); }}
        />
      )}
    </div>
  );
}

function PersonalakteForm({ akte, onClose, onSaved }) {
  const isEdit = !!akte;
  const [form, setForm] = useState({
    vorname: akte?.vorname || "", nachname: akte?.nachname || "",
    email: akte?.email || "", rolle: akte?.rolle || "dozent",
    status: akte?.status || "aktiv", eintritt_datum: akte?.eintritt_datum || "",
    austritt_datum: akte?.austritt_datum || "", austritt_grund: akte?.austritt_grund || "",
    qualifikationen: (akte?.qualifikationen || []).join(", "),
    notizen: akte?.notizen || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.vorname || !form.nachname) {
      toast.error("Vorname und Nachname sind Pflichtfelder");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        ...form,
        qualifikationen: form.qualifikationen.split(",").map(s => s.trim()).filter(Boolean),
      };
      if (isEdit) {
        await axios.patch(`${API}/personalakten/${akte.id}`, payload);
        toast.success("Personalakte aktualisiert");
      } else {
        await axios.post(`${API}/personalakten`, payload);
        toast.success(`Personalakte ${form.vorname} ${form.nachname} angelegt`);
      }
      await onSaved();
    } catch (e) {
      toast.error("Fehler beim Speichern",
        { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="akte-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Personalakte bearbeiten" : "Neue Personalakte anlegen"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Vorname *</Label>
            <Input value={form.vorname} onChange={(e) => setForm(f => ({...f, vorname: e.target.value}))}
                   data-testid="vorname-input" />
          </div>
          <div>
            <Label>Nachname *</Label>
            <Input value={form.nachname} onChange={(e) => setForm(f => ({...f, nachname: e.target.value}))}
                   data-testid="nachname-input" />
          </div>
          <div className="col-span-2">
            <Label>E-Mail</Label>
            <Input type="email" value={form.email}
                   onChange={(e) => setForm(f => ({...f, email: e.target.value}))} />
          </div>
          <div>
            <Label>Rolle</Label>
            <Select value={form.rolle} onValueChange={(v) => setForm(f => ({...f, rolle: v}))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ROLLE_LABELS).map(([k, l]) => (
                  <SelectItem key={k} value={k}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm(f => ({...f, status: v}))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="aktiv">aktiv</SelectItem>
                <SelectItem value="ausgeschieden">ausgeschieden</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Eintritt</Label>
            <Input type="date" value={form.eintritt_datum || ""}
                   onChange={(e) => setForm(f => ({...f, eintritt_datum: e.target.value}))} />
          </div>
          <div>
            <Label>Austritt</Label>
            <Input type="date" value={form.austritt_datum || ""}
                   onChange={(e) => setForm(f => ({...f, austritt_datum: e.target.value}))} />
          </div>
          {form.status === "ausgeschieden" && (
            <div className="col-span-2">
              <Label>Austrittsgrund</Label>
              <Input value={form.austritt_grund}
                     onChange={(e) => setForm(f => ({...f, austritt_grund: e.target.value}))}
                     placeholder="z. B. eigene Kündigung, Beendigung Honorarvertrag, ..." />
            </div>
          )}
          <div className="col-span-2">
            <Label>Qualifikationen (komma-getrennt)</Label>
            <Input value={form.qualifikationen}
                   onChange={(e) => setForm(f => ({...f, qualifikationen: e.target.value}))}
                   placeholder="Pflegefachkraft, ADA-Schein, Praxisanleitung, ..." />
          </div>
          <div className="col-span-2">
            <Label>Notizen</Label>
            <Textarea rows={3} value={form.notizen}
                      onChange={(e) => setForm(f => ({...f, notizen: e.target.value}))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} disabled={saving} data-testid="save-akte-btn"
                  className="bg-orange-500 hover:bg-orange-600">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? "Speichern" : "Anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PersonalakteDetail({ akte, kategorien, onClose, onEdit, onReload }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [kategorie, setKategorie] = useState("Sonstiges");
  const [beschreibung, setBeschreibung] = useState("");
  const [bundleLoading, setBundleLoading] = useState(false);

  const downloadAzavBundle = async () => {
    try {
      setBundleLoading(true);
      const res = await axios.get(
        `${API}/azav/dozenten-cv-bundle/${akte.id}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      const safeName = `${akte.vorname || ""}_${akte.nachname || ""}`.replace(/\s+/g, "_");
      link.href = url;
      link.download = `AzAV-Qualifikationen_${safeName}_${today}.zip`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("AzAV-Bundle heruntergeladen");
    } catch (e) {
      toast.error("AzAV-Bundle konnte nicht erstellt werden",
        { description: e?.response?.data?.detail || e.message });
    } finally {
      setBundleLoading(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Datei zu groß (> 10 MB)"); return;
    }
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kategorie", kategorie);
      fd.append("beschreibung", beschreibung);
      await axios.post(`${API}/personalakten/${akte.id}/anhaenge`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Datei hochgeladen");
      setBeschreibung("");
      if (fileRef.current) fileRef.current.value = "";
      await onReload();
    } catch (e) {
      toast.error("Upload fehlgeschlagen",
        { description: e?.response?.data?.detail || e.message });
    } finally { setUploading(false); }
  };

  const download = async (a) => {
    try {
      const res = await axios.get(
        `${API}/personalakten/${akte.id}/anhaenge/${a.id}/download`,
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = a.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download fehlgeschlagen"); }
  };

  const removeAnhang = async (a) => {
    if (!window.confirm(`Datei "${a.filename}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/personalakten/${akte.id}/anhaenge/${a.id}`);
      toast.success("Datei gelöscht");
      await onReload();
    } catch (e) { toast.error("Löschen fehlgeschlagen"); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="akte-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <UserCircle className="w-6 h-6 text-orange-500" />
              {akte.vorname} {akte.nachname}
              {akte.status === "aktiv"
                ? <Badge className="ml-2 bg-green-100 text-green-700">aktiv</Badge>
                : <Badge variant="secondary" className="ml-2">ausgeschieden</Badge>}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Stammdaten */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div><span className="text-slate-500">Rolle:</span> <b>{ROLLE_LABELS[akte.rolle] || akte.rolle}</b></div>
          <div><span className="text-slate-500">E-Mail:</span> {akte.email || "—"}</div>
          <div><span className="text-slate-500">Eintritt:</span> {akte.eintritt_datum || "—"}</div>
          <div><span className="text-slate-500">Austritt:</span> {akte.austritt_datum || "—"}</div>
          {akte.austritt_grund && <div className="col-span-2"><span className="text-slate-500">Austrittsgrund:</span> {akte.austritt_grund}</div>}
          {akte.qualifikationen?.length > 0 && (
            <div className="col-span-2">
              <span className="text-slate-500">Qualifikationen:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {akte.qualifikationen.map((q, i) => (
                  <Badge key={i} variant="outline">{q}</Badge>
                ))}
              </div>
            </div>
          )}
          {akte.notizen && (
            <div className="col-span-2">
              <span className="text-slate-500">Notizen:</span>
              <p className="text-slate-700 mt-1 whitespace-pre-wrap">{akte.notizen}</p>
            </div>
          )}
        </div>

        {/* AzAV-Bundle (nur für Dozenten) */}
        {akte.rolle === "dozent" && (
          <div className="border-t pt-4 mt-2">
            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <Package className="w-4 h-4 text-orange-500" /> AzAV-Einreichung
            </h3>
            <div className="flex items-start justify-between gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="text-sm text-slate-700">
                <div className="font-medium text-slate-900">Dozenten-CV + Qualifikationsnachweise (ZIP)</div>
                <p className="text-xs text-slate-500 mt-0.5">
                  Enthält automatisch generierten Lebenslauf + alle hinterlegten Nachweise – fertig für die AzAV-Einreichung.
                </p>
              </div>
              <Button
                onClick={downloadAzavBundle}
                disabled={bundleLoading}
                className="bg-orange-500 hover:bg-orange-600 flex-shrink-0"
                data-testid="download-azav-bundle-btn"
              >
                {bundleLoading
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Wird erstellt ...</>
                  : <><Download className="w-4 h-4 mr-2" /> AzAV-Bundle herunterladen</>}
              </Button>
            </div>
          </div>
        )}

        {/* Upload */}
        <div className="border-t pt-4 mt-2">
          <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Upload className="w-4 h-4" /> Neue Datei hochladen
          </h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Kategorie</Label>
                <Select value={kategorie} onValueChange={setKategorie}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {kategorien.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Beschreibung (optional)</Label>
                <Input value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)}
                       placeholder="z. B. ADA-Schein IHK Duisburg" />
              </div>
            </div>
            <Input type="file" ref={fileRef}
                   onChange={(e) => upload(e.target.files?.[0])}
                   disabled={uploading}
                   accept=".pdf,.png,.jpg,.jpeg,.docx,.doc,.xlsx"
                   data-testid="upload-input" />
            {uploading && <div className="flex items-center gap-2 text-sm text-orange-600">
              <Loader2 className="w-4 h-4 animate-spin" /> Wird hochgeladen ...
            </div>}
          </div>
        </div>

        {/* Anhänge-Liste */}
        <div className="border-t pt-4 mt-2">
          <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" /> Dokumente ({(akte.anhaenge || []).length})
          </h3>
          {(!akte.anhaenge || akte.anhaenge.length === 0) ? (
            <p className="text-sm text-slate-400 text-center py-6">Noch keine Dokumente hinterlegt</p>
          ) : (
            <div className="space-y-2">
              {akte.anhaenge.map(a => (
                <div key={a.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100"
                     data-testid={`anhang-${a.id}`}>
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <FileText className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900 text-sm truncate">{a.filename}</div>
                      <div className="flex flex-wrap items-center gap-2 mt-0.5 text-xs text-slate-500">
                        <Badge variant="outline" className="text-xs h-5">{a.kategorie}</Badge>
                        <span>{formatBytes(a.file_size)}</span>
                        <span>· {(a.uploaded_at || "").slice(0,10)}</span>
                        {a.uploaded_by && <span>· von {a.uploaded_by}</span>}
                      </div>
                      {a.beschreibung && <p className="text-xs text-slate-500 mt-0.5">{a.beschreibung}</p>}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => download(a)}
                            data-testid={`download-${a.id}`}>
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => removeAnhang(a)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-${a.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose}>Schließen</Button>
          <Button onClick={onEdit} className="bg-orange-500 hover:bg-orange-600" data-testid="edit-akte-btn">
            Bearbeiten
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
